// services/activePlayback.js
// Feature 42 — Device Playback Handoff (client).
//
// Thin client for the server-side active-playback registry. The active
// player on this device "announces" what it's playing on a heartbeat;
// other devices on the same invite poll listActive() to surface a
// "Continue on this device?" handoff banner.
//
// Design notes:
//   - announce()/clear() are fire-and-forget. The player must NEVER break
//     because this background bookkeeping failed, so every call swallows
//     its error.
//   - listActive() returns [] on any failure so callers can render
//     unconditionally without try/catch.
//   - The heartbeat is module-singleton: only one heartbeat runs at a
//     time. Starting a new one stops the previous interval first.
//
// Server contract:
//   PUT    /api/playback/active   body { source, fileKey, title, poster,
//                                        route, position, duration, playing }
//   DELETE /api/playback/active
//   GET    /api/playback/active   -> { sessions: [...] } (OTHER devices only)

import { apiFetch } from "@/services/apiClient";

/**
 * Announce the current playback target to the server. Fire-and-forget:
 * any error (network, 401, validation) is swallowed so the player is
 * never affected.
 *
 * @param {object} target { source, fileKey, title, poster, route,
 *                          position, duration, playing }
 */
export async function announce(target) {
  try {
    await apiFetch("/api/playback/active", { method: "PUT", json: target });
  } catch {
    // Background bookkeeping — never surface to the player.
  }
}

/**
 * Clear this device's active-playback entry. Fire-and-forget.
 */
export async function clear() {
  try {
    await apiFetch("/api/playback/active", { method: "DELETE" });
  } catch {
    // Swallow — the server sweeper will expire a stale entry anyway.
  }
}

/**
 * List active playback sessions on OTHER devices for the same invite.
 * Returns [] on any error so callers can render unconditionally.
 *
 * @returns {Promise<Array<object>>}
 */
export async function listActive() {
  try {
    const res = await apiFetch("/api/playback/active");
    return Array.isArray(res?.sessions) ? res.sessions : [];
  } catch {
    return [];
  }
}

// Module-level heartbeat handle. Only one heartbeat runs at a time.
let heartbeatHandle = null;

/**
 * Start announcing on an interval. `getTargetFn` is called each tick to
 * build the current target; returning null means "nothing to announce
 * right now" (e.g. duration not yet known) and the tick is skipped
 * silently — it does NOT clear the existing entry.
 *
 * Double-start safe: an existing heartbeat is stopped before the new one
 * begins. Announces once immediately, then every `intervalMs`.
 *
 * @param {() => (object|null)} getTargetFn
 * @param {{ intervalMs?: number }} [options]
 */
export function startHeartbeat(getTargetFn, { intervalMs = 10000 } = {}) {
  // Guard double-start: stop any previous heartbeat first.
  stopHeartbeat();

  const tick = () => {
    const t = getTargetFn();
    if (t) announce(t);
  };

  tick();
  heartbeatHandle = setInterval(tick, intervalMs);
}

/**
 * Stop the heartbeat started by startHeartbeat(). Safe to call when no
 * heartbeat is running. Also drops a pending state-change announce — callers
 * stop the heartbeat right before clear(), and a late announce would
 * resurrect the entry they just removed.
 */
export function stopHeartbeat() {
  if (heartbeatHandle != null) {
    clearInterval(heartbeatHandle);
    heartbeatHandle = null;
  }
  cancelStateChangeAnnounce();
}

// Trailing-debounce handle for announceStateChange().
let stateChangeHandle = null;

/**
 * Announce immediately-ish after a transport change (play / pause / seek) so a
 * follower sees it within a poll instead of waiting out the heartbeat.
 * Trailing-debounced: a scrub fires one announce, not one per event. Same
 * getTargetFn contract as startHeartbeat — a null target skips the announce.
 *
 * @param {() => (object|null)} getTargetFn
 * @param {{ delayMs?: number }} [options]
 */
export function announceStateChange(getTargetFn, { delayMs = 400 } = {}) {
  if (stateChangeHandle != null) clearTimeout(stateChangeHandle);
  stateChangeHandle = setTimeout(() => {
    stateChangeHandle = null;
    const t = getTargetFn();
    if (t) announce(t);
  }, delayMs);
}

/**
 * Drop a pending state-change announce. Safe to call when none is pending.
 */
export function cancelStateChangeAnnounce() {
  if (stateChangeHandle != null) {
    clearTimeout(stateChangeHandle);
    stateChangeHandle = null;
  }
}
